import { useMemo } from 'react'
import { BarChart, Bar, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import { usePeakHours, type PeakHourBucket } from '@/api/endpoints/analytics'
import { Section } from '@/components/ds/Section'
import { cn } from '@/lib/cn'

import styles from './PeakHoursSection.module.scss'
import { QueryErrorNotice } from '@/components/feedback/QueryErrorNotice'

export interface PeakHoursSectionProps {
  channel: string
  /** Lookback window in days — defaults to 90, server clamps to [7, 365]. */
  days?: number
  /** IANA timezone for hour bucketing. */
  tz?: string
}

/**
 * "Em que hora do dia esse canal performa melhor?" — pulls publishedAt +
 * view counts from the YouTube Data API and buckets by hour-of-day in the
 * channel's timezone. Shows a 24-bar chart (one bar per hour) of median
 * views, plus a badge highlighting the best-performing hour.
 *
 * Median is the headline metric — a single viral upload at 03h would
 * dominate the mean and produce false positives. Hours with one sample
 * still render but don't qualify for "best hour" (need >=2).
 */
export function PeakHoursSection({ channel, days = 90, tz = 'America/Sao_Paulo' }: PeakHoursSectionProps) {
  const query = usePeakHours({ channel, days, tz })

  const chartData = useMemo<ChartDatum[]>(() => {
    const hours = query.data?.hours ?? []
    const bestHour = query.data?.best_hour ?? null
    return hours.map((b) => ({
      hour: b.hour,
      label: `${String(b.hour).padStart(2, '0')}h`,
      median: b.median_views,
      mean: b.mean_views,
      samples: b.samples,
      isBest: bestHour !== null && b.hour === bestHour,
    }))
  }, [query.data])

  // Top 5 hours by median views (descending). Hours with zero samples are
  // excluded — they have no signal to rank. When fewer than 5 hours have
  // data we just show what's there.
  const topHours = useMemo<ChartDatum[]>(() => {
    return chartData
      .filter((d) => d.samples > 0)
      .slice()
      .sort((a, b) => b.median - a.median)
      .slice(0, 5)
  }, [chartData])

  const sampleSize = query.data?.sample_size ?? 0
  const subtitle = query.data
    ? `${String(query.data.days)} dias · ${String(sampleSize)} vídeo${sampleSize === 1 ? '' : 's'} · ${query.data.timezone}`
    : `Janela: ${String(days)} dias`

  return (
    <Section title="Horários de pico" description={subtitle}>
      <div className={styles.shell}>
        <p className={styles.subtitle}>
          Mediana de views por hora de publicação. Hora de pico precisa de pelo menos 2 vídeos.
        </p>

        {query.isError ? (
          <QueryErrorNotice
            error={query.error}
            label="os horários de pico"
            onRetry={() => {
              void query.refetch()
            }}
          />
        ) : query.isPending ? (
          <div className={styles.skeleton} aria-hidden />
        ) : sampleSize === 0 ? (
          <div className={styles.empty}>
            Publique alguns vídeos pra ver os horários de pico do canal.
          </div>
        ) : (
          <div className={styles.body}>
            <div className={styles.chartCol}>
              <Chart data={chartData} />
            </div>
            <RankingColumn topHours={topHours} />
          </div>
        )}
      </div>
    </Section>
  )
}

// ---------------------------------------------------------------------------
// Ranking column — top 5 hours by median, descending. Visual hierarchy
// matches the chart (#1 in green to sync with the green bar).
// ---------------------------------------------------------------------------

interface RankingColumnProps {
  topHours: ChartDatum[]
}

function RankingColumn({ topHours }: RankingColumnProps) {
  if (topHours.length === 0) return null
  return (
    <aside className={styles.ranking} aria-label="Top horários por mediana de views">
      <h3 className={styles.rankingHead}>Top horários</h3>
      <ol className={styles.rankingList}>
        {topHours.map((d, idx) => {
          const rank = idx + 1
          return (
            <li
              key={`rank-${String(d.hour)}`}
              className={cn(
                styles.rankingRow,
                rank === 1 && styles.rankingTop,
              )}
            >
              <span className={styles.rankingPos}>#{String(rank)}</span>
              <span className={styles.rankingHour}>{d.label}</span>
              <span className={styles.rankingMedian}>
                {formatCount(Math.round(d.median))}
                <small className={styles.rankingUnit}> views</small>
              </span>
              <span className={styles.rankingSamples}>
                {String(d.samples)} vídeo{d.samples === 1 ? '' : 's'}
              </span>
            </li>
          )
        })}
      </ol>
    </aside>
  )
}

// ---------------------------------------------------------------------------
// Bar chart
// ---------------------------------------------------------------------------

interface ChartDatum {
  hour: number
  label: string
  median: number
  mean: number
  samples: number
  isBest: boolean
}

function Chart({ data }: { data: ChartDatum[] }) {
  return (
    <div className={styles.chart}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
          <XAxis
            dataKey="label"
            stroke="var(--fg-subtle)"
            fontSize={10}
            tickLine={false}
            axisLine={{ stroke: 'var(--border)' }}
            interval={1}
          />
          <YAxis
            stroke="var(--fg-subtle)"
            fontSize={10}
            tickLine={false}
            axisLine={false}
            tickFormatter={formatCount}
            width={42}
          />
          <Tooltip
            cursor={{ fill: 'var(--surface-alt)', opacity: 0.4 }}
            content={<CustomTooltip />}
          />
          <Bar dataKey="median" radius={[3, 3, 0, 0]} minPointSize={2} shape={renderBar} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

// Per-bar fill via custom shape — recharts v3 deprecates <Cell> for this
// use case. Each bucket picks one of three colors: success (best hour),
// brand orange (normal), or muted surface (no samples).
interface BarShapeProps {
  x?: number
  y?: number
  width?: number
  height?: number
  payload?: ChartDatum
}

function renderBar(rawProps: unknown) {
  const props = rawProps as BarShapeProps
  const { x = 0, y = 0, width = 0, height = 0, payload } = props
  if (!payload || width <= 0) return <g />
  const empty = payload.samples === 0
  const fill = empty
    ? 'var(--surface-alt)'
    : payload.isBest
      ? 'var(--success)'
      : 'var(--brand-ignite)'
  const opacity = empty ? 0.4 : 1
  return <rect x={x} y={y} width={width} height={height} fill={fill} fillOpacity={opacity} rx={3} ry={3} />
}

// ---------------------------------------------------------------------------
// Tooltip
// ---------------------------------------------------------------------------

interface RechartsTooltipPayload {
  payload?: ChartDatum
}

function CustomTooltip({
  active,
  payload,
}: {
  active?: boolean
  payload?: RechartsTooltipPayload[]
}) {
  if (!active || !payload || payload.length === 0) return null
  const datum = payload[0]?.payload
  if (!datum) return null
  if (datum.samples === 0) {
    return (
      <div className={styles.tooltip}>
        <span className={styles.tooltipHour}>{datum.label}</span>
        <span className={styles.tooltipMuted}>Sem amostras nesta hora</span>
      </div>
    )
  }
  return (
    <div className={styles.tooltip}>
      <span className={styles.tooltipHour}>{datum.label}</span>
      <span className={styles.tooltipRow}>
        Mediana: <strong>{formatCount(Math.round(datum.median))}</strong> views
      </span>
      <span className={styles.tooltipRow}>
        Média: {formatCount(Math.round(datum.mean))} views
      </span>
      <span className={styles.tooltipRow}>
        {String(datum.samples)} vídeo{datum.samples === 1 ? '' : 's'}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}k`
  return String(n)
}

// Re-export for callers that want to render their own visualization.
export type { PeakHourBucket }
